export const NGK_OPS_SERVICES_META_KEY = '_ngk_ops_services';

export const OPS_SERVICE_TYPES = [
  { value: 'arrival', label: 'Arrival' },
  { value: 'departure', label: 'Departure' },
  { value: 'tour', label: 'Tour' },
  { value: 'transfer', label: 'Transfer' },
] as const;

export type OpsServiceType = (typeof OPS_SERVICE_TYPES)[number]['value'];

export const OPS_SERVICE_STATUSES = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

export type OpsServiceStatus = (typeof OPS_SERVICE_STATUSES)[number]['value'];

export interface OpsService {
  id: string;
  serviceDate: string; // YYYY-MM-DD
  serviceType: OpsServiceType;
  serviceName: string;
  /** Ops file / reference number */
  fileNo?: string | null;
  agency?: string | null;
  nationality?: string | null;
  pax?: number | null;
  /** Included pax (INC) — tour ops */
  includedPax?: number | null;
  /** Optional pax (OPT) — tour ops */
  optionalPax?: number | null;
  hotel?: string | null;
  pickupTime?: string | null;
  /** Operation / start time (separate from pickup) */
  serviceTime?: string | null;
  flightNumber?: string | null;
  flightTime?: string | null;
  from?: string | null;
  to?: string | null;
  guide?: string | null;
  coordinator?: string | null;
  vehicle?: string | null;
  driver?: string | null;
  notes?: string | null;
  status: OpsServiceStatus;
}

export type OpsServiceInput = Omit<OpsService, 'id'> & { id?: string };

export function createOpsServiceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ops_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function isOpsServiceType(value: unknown): value is OpsServiceType {
  return OPS_SERVICE_TYPES.some((option) => option.value === value);
}

export function isOpsServiceStatus(value: unknown): value is OpsServiceStatus {
  return OPS_SERVICE_STATUSES.some((option) => option.value === value);
}

function normalizeDate(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return '';
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function normalizeOptionalString(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function normalizeOptionalNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export function normalizeOpsService(raw: unknown): OpsService | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const serviceDate = normalizeDate(record.serviceDate);
  const serviceType = record.serviceType;
  const serviceName = normalizeOptionalString(record.serviceName) || '';
  const status = isOpsServiceStatus(record.status) ? record.status : 'scheduled';

  if (!serviceDate || !isOpsServiceType(serviceType) || !serviceName) {
    return null;
  }

  return {
    id: normalizeOptionalString(record.id) || createOpsServiceId(),
    serviceDate,
    serviceType,
    serviceName,
    fileNo: normalizeOptionalString(record.fileNo),
    agency: normalizeOptionalString(record.agency),
    nationality: normalizeOptionalString(record.nationality),
    pax: normalizeOptionalNumber(record.pax),
    includedPax: normalizeOptionalNumber(record.includedPax),
    optionalPax: normalizeOptionalNumber(record.optionalPax),
    hotel: normalizeOptionalString(record.hotel),
    pickupTime: normalizeOptionalString(record.pickupTime),
    serviceTime: normalizeOptionalString(record.serviceTime),
    flightNumber: normalizeOptionalString(record.flightNumber),
    flightTime: normalizeOptionalString(record.flightTime),
    from: normalizeOptionalString(record.from),
    to: normalizeOptionalString(record.to),
    guide: normalizeOptionalString(record.guide),
    coordinator: normalizeOptionalString(record.coordinator),
    vehicle: normalizeOptionalString(record.vehicle),
    driver: normalizeOptionalString(record.driver),
    notes: normalizeOptionalString(record.notes),
    status,
  };
}

export function parseOpsServicesFromMeta(
  metaData?: Array<{ key?: string | null; value?: string | null }> | null
): OpsService[] {
  if (!metaData?.length) return [];
  const entry = metaData.find((item) => item.key === NGK_OPS_SERVICES_META_KEY);
  if (!entry?.value) return [];

  try {
    const parsed = JSON.parse(entry.value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeOpsService)
      .filter((service): service is OpsService => !!service)
      .sort((a, b) => {
        if (a.serviceDate !== b.serviceDate) {
          return a.serviceDate.localeCompare(b.serviceDate);
        }
        const aTime = a.pickupTime || a.serviceTime || '';
        const bTime = b.pickupTime || b.serviceTime || '';
        return aTime.localeCompare(bTime);
      });
  } catch {
    return [];
  }
}

/** Prefill tour service fields from the first tour line item on a WC order. */
export function getTourPrefillFromLineItems(
  lineItems?: Array<{
    product?: { node?: { name?: string | null } | null } | null;
    metaData?: Array<{ key?: string | null; value?: string | null }> | null;
  }> | null
): Partial<OpsServiceInput> {
  if (!lineItems?.length) return {};

  // Lazy import avoided — use inline key lookup to keep this util free of circular deps
  for (const item of lineItems) {
    const meta: Record<string, string> = {};
    (item.metaData || []).forEach((entry) => {
      if (entry?.key) meta[entry.key] = entry.value || '';
    });
    const productName = item.product?.node?.name || '';
    const hasTour =
      !!meta['Tour Title'] ||
      !!meta['Tour ID'] ||
      productName.toLowerCase().includes('tour');
    if (!hasTour) continue;

    const adults = Number(meta.Adults || 0);
    const children = Number(meta.Children || 0);
    const guests = Number(meta.Guests || 0);
    const paxFromParts = adults + children;
    const pax = paxFromParts > 0 ? paxFromParts : guests > 0 ? guests : null;

    const bookingDate = meta['Booking Date'] || meta.booking_date || '';
    let serviceDate: string | undefined;
    if (bookingDate) {
      const normalized = normalizeDate(bookingDate);
      if (normalized) serviceDate = normalized;
    }

    return {
      serviceType: 'tour',
      serviceName: meta['Tour Title'] || productName || '',
      pax,
      ...(serviceDate ? { serviceDate } : {}),
    };
  }

  return {};
}

function metaFromLineItem(
  item: {
    product?: { node?: { name?: string | null } | null } | null;
    metaData?: Array<{ key?: string | null; value?: string | null }> | null;
  }
): { meta: Record<string, string>; productName: string } {
  const meta: Record<string, string> = {};
  (item.metaData || []).forEach((entry) => {
    if (entry?.key) meta[entry.key] = entry.value || '';
  });
  return { meta, productName: item.product?.node?.name || '' };
}

function parsePackageHotel(meta: Record<string, string>): string {
  if (meta._ngk_hotel_name) return meta._ngk_hotel_name;
  if (meta['tat_fields_hotel_/_accommodation']) return meta['tat_fields_hotel_/_accommodation'];
  if (meta.tat_fields_hotel_v_accomodation) return meta.tat_fields_hotel_v_accomodation;
  try {
    const pkg = meta.packageInfo ? JSON.parse(meta.packageInfo) : null;
    if (pkg && typeof pkg.hotel_name === 'string' && pkg.hotel_name.trim()) {
      return pkg.hotel_name.trim();
    }
  } catch {
    /* ignore */
  }
  return '';
}

function normalizeTimeValue(value?: string): string {
  if (!value) return '';
  const trimmed = value.trim();
  // Accept HH:mm or HH:mm:ss
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (match) {
    return `${match[1].padStart(2, '0')}:${match[2]}`;
  }
  return '';
}

/**
 * Prefill Arrival/Departure (package) service fields from transfer/hotel line items.
 */
export function getArrivalDeparturePrefillFromLineItems(
  lineItems?: Array<{
    product?: { node?: { name?: string | null } | null } | null;
    metaData?: Array<{ key?: string | null; value?: string | null }> | null;
  }> | null,
  preferredType: 'arrival' | 'departure' = 'arrival'
): Partial<OpsServiceInput> {
  if (!lineItems?.length) return {};

  for (const item of lineItems) {
    const { meta, productName } = metaFromLineItem(item);
    const name = productName.toLowerCase();
    const isTransfer =
      !!meta._tat_vehicle ||
      !!meta._tat_pickup ||
      !!meta._tat_pickup_name ||
      !!meta.tat_fields_flight_no ||
      !!meta._tat_extra_fields ||
      name.includes('transfer') ||
      name.includes('airport');
    const isHotelPackage =
      meta._ngk_hotel_booking === 'yes' ||
      !!meta._ngk_hotel_name ||
      !!meta.packageInfo;

    if (!isTransfer && !isHotelPackage) continue;

    const adults = Number(meta._tat_adults || meta.Adults || 0);
    const children = Number(meta._tat_children || meta.Children || 0);
    let pax: number | null = adults + children > 0 ? adults + children : null;
    if (pax == null && meta.packageInfo) {
      try {
        const pkg = JSON.parse(meta.packageInfo);
        const total =
          Number(pkg.totalAdults || 0) + Number(pkg.totalChildren || 0);
        if (total > 0) pax = total;
      } catch {
        /* ignore */
      }
    }

    const hasArrivalMeta =
      !!meta.tat_fields_arrival_date || !!meta.tat_fields_arrival_time;
    const hasDepartureMeta =
      !!meta.tat_fields_departure_date || !!meta.tat_fields_departure_time;
    let serviceType: 'arrival' | 'departure' = preferredType;
    if (preferredType === 'arrival' && !hasArrivalMeta && hasDepartureMeta) {
      serviceType = 'departure';
    } else if (preferredType === 'departure' && !hasDepartureMeta && hasArrivalMeta) {
      serviceType = 'arrival';
    } else if (hasArrivalMeta && !hasDepartureMeta) {
      serviceType = 'arrival';
    } else if (hasDepartureMeta && !hasArrivalMeta) {
      serviceType = 'departure';
    }

    const dateRaw =
      serviceType === 'arrival'
        ? meta.tat_fields_arrival_date || meta._tat_arrival_date
        : meta.tat_fields_departure_date || meta._tat_departure_date;
    const timeRaw =
      serviceType === 'arrival'
        ? meta.tat_fields_arrival_time
        : meta.tat_fields_departure_time;

    const serviceDate = dateRaw ? normalizeDate(dateRaw) : '';
    const flightTime = normalizeTimeValue(timeRaw);

    return {
      serviceType,
      serviceName: serviceType === 'arrival' ? 'Arrival' : 'Departure',
      pax,
      hotel: parsePackageHotel(meta) || null,
      flightNumber: meta.tat_fields_flight_no || null,
      from: meta.tat_fields_from || null,
      to: meta.tat_fields_to || null,
      flightTime: flightTime || null,
      ...(serviceDate ? { serviceDate } : {}),
      status: 'scheduled',
    };
  }

  return {
    serviceType: preferredType,
    serviceName: preferredType === 'arrival' ? 'Arrival' : 'Departure',
    status: 'scheduled',
  };
}

export function serializeOpsServices(services: OpsService[]): string {
  return JSON.stringify(services);
}

export function upsertOpsService(
  services: OpsService[],
  input: OpsServiceInput
): OpsService[] {
  const normalized = normalizeOpsService({
    ...input,
    id: input.id || createOpsServiceId(),
  });
  if (!normalized) return services;

  const index = services.findIndex((service) => service.id === normalized.id);
  if (index === -1) return [...services, normalized];
  const next = [...services];
  next[index] = normalized;
  return next;
}

export function removeOpsService(services: OpsService[], serviceId: string): OpsService[] {
  return services.filter((service) => service.id !== serviceId);
}

export function filterOpsServicesByDate(
  services: OpsService[],
  date: string,
  options?: { includeCancelled?: boolean }
): OpsService[] {
  const includeCancelled = options?.includeCancelled ?? false;
  return services.filter((service) => {
    if (service.serviceDate !== date) return false;
    if (!includeCancelled && service.status === 'cancelled') return false;
    return true;
  });
}

export function getOpsServiceTypeLabel(type: OpsServiceType): string {
  return OPS_SERVICE_TYPES.find((option) => option.value === type)?.label || type;
}

export function getOpsServiceStatusLabel(status: OpsServiceStatus): string {
  return OPS_SERVICE_STATUSES.find((option) => option.value === status)?.label || status;
}

export function getOpsServiceStatusColor(status: OpsServiceStatus): string {
  if (status === 'completed') return 'success';
  if (status === 'cancelled') return 'danger';
  if (status === 'in_progress') return 'primary';
  if (status === 'assigned') return 'info';
  return 'secondary';
}

export function formatOpsServiceDate(date: string): string {
  if (!date) return '—';
  try {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return date;
  }
}

export function toInputDateValue(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function shiftInputDate(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const next = new Date(y, m - 1, d);
  next.setDate(next.getDate() + days);
  return toInputDateValue(next);
}
